import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Zap, Globe, Play, Award, BadgeCheck, Scale, HeartPulse } from 'lucide-react';
import { useScrollReveal, useCountUp } from '../hooks/useAnimations';
import { useQuote } from '../context/QuoteContext';

/* ─── Reusable reveal wrapper ─── */
const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`transition-all duration-[800ms] cubic-bezier(0.16,1,0.3,1) ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

/* ─── Animated stat counter ─── */
const StatCounter: React.FC<{ end: number; suffix?: string; label: string }> = ({ end, suffix = '', label }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  const count = useCountUp(end, 1800, visible);
  return (
    <div ref={ref} className="text-center p-8 lg:p-10 rounded-2xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] transition-all duration-500 group">
      <div className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-3 tabular-nums">
        {visible ? count : 0}<span className="text-brand-400">{suffix}</span>
      </div>
      <div className="text-xs font-semibold text-silver-400 uppercase tracking-[0.15em]">{label}</div>
    </div>
  );
};

const Home: React.FC = () => {
  const { openQuote } = useQuote();
  return (
    <main className="bg-white overflow-hidden">

      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <section className="relative min-h-screen flex items-center">
        {/* Ambient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-white to-silver-50" />
        <div className="absolute inset-0 dot-grid opacity-[0.35]" />

        {/* Animated gradient orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[700px] h-[700px] bg-gradient-to-br from-brand-200/40 to-brand-400/10 rounded-full blur-[100px] animate-pulse-soft" />
          <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-gradient-to-tr from-brand-100/30 to-transparent rounded-full blur-[80px] animate-pulse-soft" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-brand-300/10 rounded-full blur-[120px] animate-glow-pulse" />
        </div>

        {/* Decorative rings */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] border border-brand-200/20 rounded-full animate-spin-slow" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] border border-brand-100/10 rounded-full animate-spin-slow" style={{ animationDirection: 'reverse' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-24 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left — Copy */}
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2.5 bg-white/80 backdrop-blur-sm border border-brand-100/60 rounded-full px-4 py-2 mb-8 animate-fade-in shadow-sm">
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
                </span>
                <span className="text-[11px] font-semibold text-brand-700 uppercase tracking-[0.15em]">7-Stage Purified · 100% Automated</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl font-extrabold text-silver-950 leading-[1.08] tracking-tight animate-slide-up">
                Pure Water,{' '}
                <span className="text-gradient">Refined by Science</span>,{' '}
                Born in Nature.
              </h1>

              <p className="mt-7 text-lg text-silver-500 leading-relaxed max-w-md animate-slide-up-delayed">
                Arohix Water by Nathkrupa Energy — the next generation of premium packaged drinking water, engineered with a 7-stage multi-barrier purification process.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4 animate-slide-up-delayed-2">
                <Link to="/science" className="shimmer-btn group inline-flex items-center gap-2.5 bg-silver-950 text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-silver-800 hover:shadow-elevated transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]">
                  Discover Our Science
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link to="/products" className="group inline-flex items-center gap-2 text-sm font-semibold text-silver-600 hover:text-brand-600 transition-all duration-300">
                  <span className="w-10 h-10 rounded-full bg-brand-50 border border-brand-100 flex items-center justify-center group-hover:bg-brand-100 group-hover:scale-105 transition-all duration-300">
                    <Play className="w-3.5 h-3.5 text-brand-600 ml-0.5" />
                  </span>
                  View Products
                </Link>
              </div>

              {/* Trust badges inline */}
              <div className="mt-12 flex items-center gap-5 animate-fade-in" style={{ animationDelay: '0.6s' }}>
                {[
                  { label: 'ISO', icon: <Award className="w-3.5 h-3.5 text-brand-600" /> },
                  { label: 'FSSAI', icon: <BadgeCheck className="w-3.5 h-3.5 text-brand-600" /> },
                  { label: 'BIS', icon: <Scale className="w-3.5 h-3.5 text-brand-600" /> },
                  { label: 'WHO', icon: <HeartPulse className="w-3.5 h-3.5 text-brand-600" /> },
                ].map((badge) => (
                  <div key={badge.label} className="flex items-center gap-1.5">
                    <div className="w-6 h-6 rounded-md bg-brand-50 border border-brand-100/60 flex items-center justify-center">
                      {badge.icon}
                    </div>
                    <span className="text-[10px] font-medium text-silver-400 hidden sm:inline">{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Bottle Image */}
            <div className="relative flex items-center justify-center">
              {/* Glow behind bottle */}
              <div className="absolute w-[350px] h-[350px] bg-brand-400/12 rounded-full blur-[100px] animate-glow-pulse" />

              <div className="relative animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <div className="relative mx-auto animate-float">
                  <div className="bg-gradient-to-br from-sky-100/60 via-brand-50/40 to-sky-50/60 rounded-[2rem] p-8 shadow-xl shadow-brand-200/10 border border-white/60">
                    <img
                      src="/assets/aerohix-water-1l.jpeg"
                      alt="Arohix Packaged Drinking Water"
                      className="h-[380px] w-auto object-contain drop-shadow-2xl rounded-2xl"
                    />
                  </div>
                </div>

                {/* Floating badges */}
                <div className="absolute -left-24 top-12 bg-white rounded-2xl shadow-elevated border border-silver-100/60 px-5 py-3.5 animate-float hover-lift cursor-default" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center shadow-sm">
                      <ShieldCheck className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-silver-900">ISO 9001:2015</div>
                      <div className="text-[10px] text-silver-400 font-medium">Certified Quality</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -right-24 top-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-elevated border border-silver-100/60 px-5 py-3.5 animate-float hover-lift cursor-default" style={{ animationDelay: '2s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center shadow-sm">
                      <span className="text-xs font-black text-teal-600">pH</span>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-silver-900">pH 7.2 – 7.5</div>
                      <div className="text-[10px] text-silver-400 font-medium">Naturally Balanced</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -left-16 -bottom-4 bg-white rounded-2xl shadow-elevated border border-silver-100/60 px-5 py-3.5 animate-float hover-lift cursor-default" style={{ animationDelay: '3s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-100 to-sky-200 flex items-center justify-center shadow-sm">
                      <span className="text-[11px] font-black text-sky-600">O₃</span>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-silver-900">Dual Ozonated</div>
                      <div className="text-[10px] text-silver-400 font-medium">2× Freshness</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 inset-x-0">
          <svg viewBox="0 0 1440 80" fill="none" className="w-full" preserveAspectRatio="none">
            <path d="M0 80V40C240 10 480 0 720 20C960 40 1200 30 1440 10V80H0Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ═══════════════════════ VIDEO — SEE IT IN ACTION ═══════════════════════ */}
      <section className="relative overflow-hidden">
        <div className="relative h-[85vh] min-h-[500px]">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            src="/assets/videos/Aerohix-water-video.mp4"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-silver-950/85 via-silver-950/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/30 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Reveal>
              <div className="text-center px-6">
                <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-300 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-5">
                  Arohix Water
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
                  Premium Hydration,{' '}
                  <span className="bg-gradient-to-r from-brand-300 to-sky-300 bg-clip-text text-transparent">Bottled to Perfection</span>
                </h2>
                <p className="mt-4 text-brand-100/70 text-lg max-w-xl mx-auto">
                  7-stage purified, mineral-enriched and sealed for freshness — because what you drink matters.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ FEATURES ═══════════════════════ */}
      <section className="py-16 lg:py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Why Arohix</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-silver-950 tracking-tight">
                Engineered for <span className="text-gradient">Absolute Purity</span>
              </h2>
              <p className="mt-5 text-silver-500 max-w-lg mx-auto text-lg">
                Every bottle reflects our commitment to science-backed purification and zero-compromise quality.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: <Zap className="w-6 h-6 text-amber-500" />,
                title: '100% Automated Bottling',
                description: 'Zero human contact throughout the entire purification and bottling process ensures absolute hygiene.',
                metric: '0 Touch',
                bg: 'from-amber-50 to-orange-50',
              },
              {
                icon: <ShieldCheck className="w-6 h-6 text-brand-500" />,
                title: '7-Stage Multi-Barrier',
                description: 'From Ozonation AOP to mineral enrichment — our multi-barrier system removes 99.99% of contaminants.',
                metric: '7 Stages',
                bg: 'from-brand-50 to-blue-50',
              },
              {
                icon: <Globe className="w-6 h-6 text-emerald-500" />,
                title: 'Sustainable Sourcing',
                description: 'Responsibly sourced from pristine hillside origins, processed with eco-conscious methods.',
                metric: 'Eco First',
                bg: 'from-emerald-50 to-teal-50',
              },
            ].map((feature, i) => (
              <Reveal key={feature.title} delay={i * 150}>
                <div className="group relative bg-white border border-silver-100 rounded-2xl p-8 hover:shadow-card-hover hover:border-silver-200/80 hover:-translate-y-1 transition-all duration-500 gradient-border h-full">
                  <div className="flex items-center justify-between mb-7">
                    <div className={`w-13 h-13 rounded-2xl bg-gradient-to-br ${feature.bg} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      {feature.icon}
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.15em] text-brand-400 opacity-60">{feature.metric}</span>
                  </div>
                  <h3 className="text-xl font-bold text-silver-900 mb-3">{feature.title}</h3>
                  <p className="text-sm text-silver-500 leading-relaxed">{feature.description}</p>
                  <div className="mt-6 h-px bg-gradient-to-r from-silver-100 via-silver-200 to-silver-100 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ STATS ═══════════════════════ */}
      <section className="relative py-16 lg:py-20 bg-silver-950 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-brand-600/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-brand-500/5 rounded-full blur-[100px]" />
          {/* Grid lines */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-400 bg-brand-400/10 px-4 py-1.5 rounded-full mb-4">By the Numbers</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
                Precision at Scale
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            <StatCounter end={7} label="Purification Stages" />
            <StatCounter end={100} suffix="%" label="Automation Rate" />
            <StatCounter end={0} label="Human Touch Points" />
            <div className="text-center p-8 lg:p-10 rounded-2xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] transition-all duration-500">
              <div className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-3">24/7</div>
              <div className="text-xs font-semibold text-silver-400 uppercase tracking-[0.15em]">Quality Monitoring</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ PRODUCT SHOWCASE ═══════════════════════ */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-white via-silver-50/30 to-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Our Range</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-silver-950 tracking-tight">
                Water for Every <span className="text-gradient">Occasion</span>
              </h2>
              <p className="mt-5 text-silver-500 max-w-lg mx-auto text-lg">
                From events to daily hydration — Arohix delivers 7-stage purified water in 200ml, 500ml & 1L formats.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                name: 'Arohix 200ml',
                sizes: '200ml',
                description: 'Compact hydration for events, conferences, weddings & on-the-go moments.',
                image: '/assets/aerohix-water-200ml.png',
              },
              {
                name: 'Arohix 500ml',
                sizes: '500ml',
                description: 'Your everyday companion. 7-stage purified water with active minerals.',
                image: '/assets/aerohix-water-500ml.png',
              },
              {
                name: 'Arohix 1L',
                sizes: '1 Litre',
                description: 'Family-size purity for home, office & institutional supply.',
                image: '/assets/aerohix-water-1l.jpeg',
              },
            ].map((product, i) => (
              <Reveal key={product.name} delay={i * 150}>
                <Link to="/products" className="group block bg-white border border-silver-100 rounded-2xl overflow-hidden hover:shadow-card-hover hover:border-silver-200/80 hover:-translate-y-1 transition-all duration-500 gradient-border h-full">
                  {/* Bottle area */}
                  <div className="relative bg-gradient-to-b from-sky-50/50 to-white overflow-hidden aspect-[4/3]">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Info */}
                  <div className="p-7">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-silver-900 group-hover:text-brand-700 transition-colors">{product.name}</h3>
                      <span className="text-[10px] font-bold text-brand-600 bg-brand-50 px-2.5 py-1 rounded-full">{product.sizes}</span>
                    </div>
                    <p className="text-sm text-silver-500 leading-relaxed">{product.description}</p>
                    <div className="mt-5 flex items-center gap-2 text-xs font-semibold text-silver-400 group-hover:text-brand-600 transition-colors">
                      Learn More
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ B2B CTA ═══════════════════════ */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800" />
        {/* Decorative */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/[0.03] rounded-full" />
          <div className="absolute top-10 right-10 w-[300px] h-[300px] bg-white/5 rounded-full blur-[80px]" />
          <div className="absolute bottom-10 left-10 w-[200px] h-[200px] bg-brand-400/10 rounded-full blur-[60px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-200/80 bg-white/10 px-4 py-1.5 rounded-full mb-6">Partner with Us</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Bulk Supply & Custom Branding for Your Business
            </h2>
            <p className="mt-6 text-lg text-brand-100/70 max-w-2xl mx-auto leading-relaxed">
              Hotels, hospitals, corporates, events — Arohix offers scalable supply with custom-branded packaging for B2B partners across India.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button onClick={openQuote} className="shimmer-btn group inline-flex items-center gap-2.5 bg-white text-brand-700 px-8 py-4 rounded-full text-sm font-bold hover:bg-brand-50 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]">
                Become a Distributor
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <Link to="/products" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors duration-300">
                Explore Products <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
};

export default Home;
